/**
 * AdminAIUsage — Phase 14 Wave 1, Module F (read-only)
 * Top-line AI call metrics over time, backed by `/api/admin/ai-calls`.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Brain, Loader2, RefreshCw, Zap } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminAIUsage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ai-calls", { params: { limit: 100 } });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setItems([]); setTotal(0);
    } finally { setLoading(false); }
  }, []);

  const [retrying, setRetrying] = useState(false);
  const retryFailures = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post("/admin/ai/retry-failures", null, { params: { hours: 24, limit: 20 } });
      const ok = data?.succeeded ?? 0;
      const total = data?.attempted ?? 0;
      toast.success(`Retried ${total} failed AI call(s) — ${ok} recovered`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const summary = items.reduce(
    (acc, r) => {
      acc.total += 1;
      acc.tokens += (r.input_tokens || 0) + (r.output_tokens || 0);
      acc.latency += r.latency_ms || 0;
      if (r.status && r.status !== "ok") acc.errors += 1;
      acc.byModel[r.model || "unknown"] = (acc.byModel[r.model || "unknown"] || 0) + 1;
      return acc;
    },
    { total: 0, tokens: 0, latency: 0, errors: 0, byModel: {} }
  );
  const avgLatency = summary.total ? Math.round(summary.latency / summary.total) : 0;

  return (
    <div className="space-y-6 fade-in" data-testid="admin-ai-usage">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">AI</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">AI Usage</h1>
          <p className="mt-1 text-sm text-slate-500">Last 100 calls — tokens, latency, model split.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ai-refresh">
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={retryFailures}
          disabled={retrying || summary.errors === 0}
          data-testid="ai-retry-failures"
          className="border-amber-200 text-amber-700 hover:bg-amber-50"
        >
          {retrying ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Zap className="mr-1 h-3.5 w-3.5" />}
          Retry failed (24h)
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-500">Calls (showing)</p>
          <p className="mt-2 text-2xl font-bold text-slate-900">{summary.total}</p>
          <p className="text-xs text-slate-500">of {total} total</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-500">Tokens</p>
          <p className="mt-2 text-2xl font-bold text-slate-900">{summary.tokens.toLocaleString()}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-wide text-slate-500">Avg latency</p>
          <p className="mt-2 text-2xl font-bold text-slate-900">{avgLatency} ms</p>
        </div>
        <div className={`rounded-xl border p-4 shadow-sm ${summary.errors > 0 ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}`}>
          <p className="text-xs uppercase tracking-wide text-slate-500">Errors</p>
          <p className="mt-2 text-2xl font-bold text-slate-900">{summary.errors}</p>
          <p className="text-xs text-slate-500">{summary.total ? Math.round(summary.errors / summary.total * 100) : 0}%</p>
        </div>
      </div>

      {Object.keys(summary.byModel).length ? (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-900">By model</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(summary.byModel).map(([m, c]) => (
              <span key={m} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                <Brain className="h-3 w-3" /> {m} · {c}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2.5 text-left">When</th>
              <th className="px-3 py-2.5 text-left">User</th>
              <th className="px-3 py-2.5 text-left">Model</th>
              <th className="px-3 py-2.5 text-right">Input</th>
              <th className="px-3 py-2.5 text-right">Output</th>
              <th className="px-3 py-2.5 text-right">Latency</th>
              <th className="px-3 py-2.5 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan="7" className="px-3 py-12 text-center text-slate-400"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>
            ) : !items.length ? (
              <tr><td colSpan="7" className="px-3 py-12 text-center text-slate-400">No AI calls yet.</td></tr>
            ) : items.map((r) => (
              <tr key={r.id || r._id || `${r.created_at}-${Math.random()}`} className="hover:bg-slate-50">
                <td className="px-3 py-2.5 text-xs text-slate-600">{fmtDate(r.created_at)}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{(r.user_id || "—").slice(0, 8)}</td>
                <td className="px-3 py-2.5 text-xs">{r.model || "—"}</td>
                <td className="px-3 py-2.5 text-right text-xs">{r.input_tokens ?? 0}</td>
                <td className="px-3 py-2.5 text-right text-xs">{r.output_tokens ?? 0}</td>
                <td className="px-3 py-2.5 text-right text-xs">{r.latency_ms ?? 0} ms</td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${r.status === "ok" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                    {r.status || "ok"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
